import React from "react";
import {
  Zap,
  Award,
  Rocket,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";

/**
 * MagazineAd — a full-page (US Letter, 8.5" x 11") print-ready ad targeted
 * at home builders and home services owners. White background, brand
 * palette accents. View at /print/magazine-ad. Print via browser (Cmd/Ctrl+P
 * → "Save as PDF" → Letter portrait, no margins) for a print-ready file.
 */

const ValueTile = ({ icon: Icon, title, body }) => (
  <div className="p-6 border border-[#002d4a]/12 rounded-2xl bg-white">
    <div className="w-10 h-10 rounded-full bg-[#b7eb1b] flex items-center justify-center mb-4">
      <Icon size={20} className="text-[#002d4a]" strokeWidth={2.2} />
    </div>
    <div className="font-display text-xl text-[#002d4a] font-bold mb-2 leading-tight">
      {title}
    </div>
    <p className="text-sm text-[#002d4a]/75 leading-relaxed">{body}</p>
  </div>
);

const AdSample = ({ src, label }) => (
  <div className="flex flex-col">
    <div className="aspect-video rounded-xl overflow-hidden bg-[#002d4a]/5 border border-[#002d4a]/10">
      <img src={src} alt={label} className="w-full h-full object-cover" />
    </div>
    <div className="mt-2 font-eyebrow text-[10px] uppercase tracking-widest text-[#002d4a]/70">
      {label}
    </div>
  </div>
);

const MagazineAd = () => {
  return (
    <>
      <style>{`
        @page { size: Letter portrait; margin: 0; }
        @media print {
          html, body { background: #ffffff !important; }
          .no-print { display: none !important; }
        }
        .mag-page {
          width: 8.5in;
          min-height: 11in;
          background: #ffffff;
          color: #002d4a;
          margin: 0 auto;
          padding: 0.5in 0.6in;
          box-sizing: border-box;
          position: relative;
          overflow: hidden;
        }
        .mag-page::before {
          /* subtle lime accent stripe on the left edge */
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          width: 6px;
          height: 100%;
          background: #b7eb1b;
        }
        .font-display { font-family: "Bricolage Grotesque", "Instrument Sans", sans-serif; letter-spacing: -0.02em; }
        .font-eyebrow { font-family: "Instrument Sans", sans-serif; }
      `}</style>

      {/* Screen-only frame wrapper (so the page shows nicely against a
          neutral backdrop when viewed in a browser, but disappears on
          print). */}
      <div className="min-h-screen w-full bg-[#e9edf0] py-10 print:py-0 print:bg-white">
        <div className="mx-auto shadow-2xl print:shadow-none" style={{ width: "8.5in" }}>
          <div className="mag-page">
            {/* HEADER — logo + tagline */}
            <div className="flex items-center justify-between mb-9">
              <img
                src="/icon-512.png"
                alt="Anne Flinn Advertising"
                className="w-16 h-16"
              />
              <div className="text-right">
                <div className="font-eyebrow text-[10px] uppercase tracking-[0.22em] text-[#002d4a]/60">
                  Our tagline
                </div>
                <div className="font-display text-2xl font-bold text-[#002d4a] mt-1">
                  Advertise Smarter{" "}
                  <span className="text-[#7fa910]">with Us.</span>
                </div>
              </div>
            </div>

            {/* HERO */}
            <div className="mb-9">
              <div className="font-eyebrow text-[11px] uppercase tracking-[0.24em] text-[#7fa910] font-semibold mb-4">
                For Home Builders &amp; Home-Service Owners
              </div>
              <h1 className="font-display font-bold text-[46px] leading-[1.02] text-[#002d4a] mb-5">
                Your marketing,{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">handled.</span>
                  <span
                    className="absolute inset-x-0 bottom-1 h-3 bg-[#b7eb1b] -z-0"
                    aria-hidden="true"
                  />
                </span>
                <br />
                Your calendar,{" "}
                <span className="text-[#7fa910]">filled.</span>
              </h1>
              <p className="text-base text-[#002d4a]/80 leading-relaxed max-w-[5.6in]">
                We&apos;re a boutique Central Virginia agency that has spent
                years running proven campaigns for HVAC, plumbing, roofing,
                electrical, remodeling, lawn, and pest-control businesses.
                You bring the trade. We bring the plan, the ads, the buy, and
                the reporting.
              </p>
            </div>

            {/* VALUE TILES */}
            <div className="grid grid-cols-2 gap-4 mb-9">
              <ValueTile
                icon={Rocket}
                title="Easy to Start"
                body="Time-tested starter packages ready to launch. Skip the trial-and-error — pick a package and you're live."
              />
              <ValueTile
                icon={Award}
                title="Home-Services Experts"
                body="Years of proven campaigns in every major trade. We already know what works in your category, your season, your zip codes."
              />
              <ValueTile
                icon={Zap}
                title="Latest Tech, Handled"
                body="Streaming TV, PMAX Ads across all of Google, and hyper-local paid social — all managed in-house by our team."
              />
              <ValueTile
                icon={ShieldCheck}
                title="One Team, Zero Hassle"
                body="Strategy, production, media buy, and monthly reporting — one agency, one plan, one point of contact."
              />
            </div>

            {/* AD SAMPLES STRIP */}
            <div className="mb-9">
              <div className="flex items-baseline justify-between mb-4">
                <div className="font-eyebrow text-[11px] uppercase tracking-[0.24em] text-[#7fa910] font-semibold">
                  See the work
                </div>
                <div className="font-eyebrow text-[10px] uppercase tracking-widest text-[#002d4a]/60">
                  Full gallery at services.anneflinn.com/sample-ads
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <AdSample
                  src="/media/reel/ahip-roof.jpg"
                  label="Roofing · Display Banner"
                />
                <AdSample
                  src="/media/reel/bellaterra-landscapes.jpg"
                  label="Landscaping · Display Ad"
                />
                <AdSample
                  src="/media/reel/atlantic-elevators-1.jpg"
                  label="Home Access · Display Ad"
                />
              </div>
            </div>

            {/* CONTACT BLOCK */}
            <div className="mt-auto border-t-2 border-[#b7eb1b] pt-6">
              <div className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-5">
                  <img
                    src="/media/team/anne-collean.webp"
                    alt="Anne &amp; Collean"
                    className="w-24 h-24 rounded-full object-cover border-2 border-[#b7eb1b] flex-shrink-0"
                  />
                  <div>
                    <div className="font-eyebrow text-[10px] uppercase tracking-[0.22em] text-[#002d4a]/60 mb-1">
                      Call the team that answers
                    </div>
                    <div className="font-display font-bold text-xl text-[#002d4a] leading-tight">
                      Talk to Anne &amp; Collean
                    </div>
                    <div className="text-sm text-[#002d4a]/70 mt-1">
                      Free 20-minute market audit. No pressure, no theater.
                    </div>
                  </div>
                </div>

                <div className="flex-shrink-0 text-right">
                  <div className="font-eyebrow text-[10px] uppercase tracking-[0.22em] text-[#002d4a]/60 mb-1">
                    Visit
                  </div>
                  <div className="font-display font-bold text-2xl text-[#002d4a] leading-none">
                    services
                    <span className="text-[#7fa910]">.anneflinn.com</span>
                  </div>
                  <div className="mt-3 inline-flex items-center gap-2 bg-[#002d4a] text-[#b7eb1b] px-4 py-2 rounded-full font-eyebrow text-xs uppercase tracking-widest font-semibold">
                    Let&apos;s Talk <ArrowRight size={12} strokeWidth={2.5} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Screen-only print helper */}
        <div className="no-print max-w-[8.5in] mx-auto mt-6 text-center text-xs text-[#002d4a]/60">
          Preview of a print-ready 8.5&Prime; &times; 11&Prime; magazine ad.
          Use your browser&apos;s <strong>Print</strong> menu → <em>Save as
          PDF</em> → Letter, Portrait, no margins → for a press-ready file.
        </div>
      </div>
    </>
  );
};

export default MagazineAd;
